import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cartAPI, wishlistAPI } from '../../config/api';
import resolveImageUrl from '../../utils/resolveImageUrl';
import { buildDefaultBookingPayload, formatMoney } from '../../utils/rentalFields';
import { showAppToast } from '../../utils/notifications';
import swal from 'sweetalert';

function Wishlist() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const response = await wishlistAPI.list();
      setWishlistItems(response.data || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (itemId) => {
    const result = await swal({
      title: 'Remove from Wishlist?',
      text: 'Are you sure you want to remove this vehicle?',
      icon: 'warning',
      buttons: ['Cancel', 'Remove'],
      dangerMode: true,
    });

    if (!result) return;

    setActionLoading(true);
    try {
      await wishlistAPI.delete(itemId);
      showAppToast({
        message: 'Car has been removed from wishlist',
        type: 'success',
        duration: 2400,
      });
      fetchWishlist();
    } catch (error) {
      showAppToast({
        message: error.response?.data?.message || 'Failed to remove item',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleMoveToCart = async (item) => {
    const productId = item.product?.id || item.productId;
    if (!productId) return;

    setActionLoading(true);
    try {
      await cartAPI.add(buildDefaultBookingPayload(productId));
      await wishlistAPI.delete(item.id);

      showAppToast({
        message: 'Moved to cart. We added a one-day default booking; open vehicle details to choose different dates.',
        type: 'success',
        duration: 3200,
      });

      fetchWishlist();
    } catch (error) {
      showAppToast({
        message: error.response?.data?.message || 'Failed to move to cart',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="booking-page px-4 py-16 text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-luxury border-t-transparent" />
        <p className="mt-4 text-secondary-theme">Loading wishlist...</p>
      </div>
    );
  }

  if (!wishlistItems.length) {
    return (
      <div className="booking-page px-4 py-16">
        <div className="mx-auto max-w-md text-center">
          <div className="empty-booking-icon">
            <i className="bi bi-heart" />
          </div>
          <h1 className="section-title text-3xl text-primary-theme">Wishlist is Empty</h1>
          <p className="mt-3 text-secondary-theme">
            Save your favorite vehicles here for easy access later.
          </p>
          <Link to="/vehicles" className="btn-luxury mt-8">
            Browse Vehicles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <p className="text-accent">Saved Vehicles</p>
          <h1 className="section-title text-4xl text-primary-theme">Your Wishlist</h1>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {wishlistItems.map((item) => {
            const product = item.product || {};
            const detailPath = `/vehicle/show/${product.id}/${encodeURIComponent(product.name || 'vehicle')}`;

            return (
              <article key={item.id} className="wishlist-card card-luxury">
                <Link to={detailPath} className="wishlist-image-wrap">
                  <img
                    src={resolveImageUrl(product.imageURL)}
                    alt={product.name || 'Vehicle'}
                    className="wishlist-image"
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => handleRemoveFromWishlist(item.id)}
                  disabled={actionLoading}
                  className="wishlist-remove"
                  title="Remove from wishlist"
                >
                  <i className="bi bi-heart-fill" />
                </button>

                <div className="p-5">
                  <h2 className="card-title text-2xl text-primary-theme">{product.name}</h2>
                  <p className="mt-2 min-h-12 text-secondary-theme">
                    {product.description || 'Premium vehicle available for rent'}
                  </p>
                  <p className="mt-4 text-secondary-theme">
                    From <strong className="text-2xl text-accent">{formatMoney(product.price)}</strong> / day
                  </p>

                  <div className="mt-5 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => handleMoveToCart(item)}
                      disabled={actionLoading}
                      className="btn-luxury"
                    >
                      Add to Cart
                    </button>
                    <Link to={detailPath} className="btn-luxury-outline text-center">
                      View Details
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Wishlist;
